import React, { useEffect } from 'react';
import {
  HtmlEditor,
  Image,
  Inject,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
  ImportExport,
} from '@syncfusion/ej2-react-richtexteditor';

// CSS for RTE missing icons fix
import '@syncfusion/ej2-splitbuttons/styles/material3.css';
import '@syncfusion/ej2-richtexteditor/styles/material3.css';
import '@syncfusion/ej2-icons/styles/material3.css';
import { exportRteWordPdf } from 'components/campaigns/requests';

// Outside the component: This stays in memory once for the whole app
let isXhrPatched = false;
const originalOpen = XMLHttpRequest.prototype.open;
const originalSend = XMLHttpRequest.prototype.send;
const requestUrlSymbol = Symbol('requestUrl');

/**
 * Rich text editor component with:
 * - Word import support
 * - Word/PDF export support
 * - Image upload support
 * - Optional include switch
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.activeSwitch - Current state of include switch
 * @param {boolean} props.asteriskOn - Shows required field asterisk
 * @param {boolean} props.disabled - Disables editor input
 * @param {string} props.errorMessage - Validation error message
 * @param {string|number} props.height - Editor height
 * @param {string} props.label - Field label
 * @param {string} props.name - Input name/id
 * @param {Function} props.onChange - Callback triggered on editor value change
 * @param {Function} props.onSwitch - Callback triggered when include switch changes
 * @param {boolean} props.showIncludeSwitch - Toggles include switch visibility
 * @param {Object} props.style - Custom editor styles
 * @param {string} props.value - Editor HTML content
 * @returns {JSX.Element}
 */

function RichTextEditorInput({
  activeSwitch,
  asteriskOn,
  disabled,
  errorMessage,
  height,
  label,
  name,
  onChange,
  onSwitch,
  showIncludeSwitch,
  style,
  value,
}) {

  const userIdentity = JSON.parse(sessionStorage.getItem('userIdentity'));
  const token = userIdentity?.token;

  /**
   * Patches XMLHttpRequest once globally in order to:
   * - Track outgoing request URLs
   * - Inject Authorization header for Word import endpoint
   *
   * This is required because Syncfusion import requests
   * do not expose a direct way to add auth headers.
   */
  useEffect(() => {
    // Only patch if it hasn't been patched yet
    if (!isXhrPatched) {
      XMLHttpRequest.prototype.open = function (method, url) {
        this[requestUrlSymbol] = url;
        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function (body) {
        const url = this[requestUrlSymbol];

        // Add bearer token only to Word import requests
        if (url && url.includes('Campaign/import-word')) {

          // Set authorization header on request instance
          if (token) {
            this.setRequestHeader('Authorization', `Bearer ${token}`);
          }
        }
        return originalSend.apply(this, arguments);
      };

      isXhrPatched = true;
    }
  }, [token]);


  /**
   * Handles exporting editor content as:
   * - Word (.docx)
   * - PDF (.pdf)
   *
   * Steps:
   * 1. Sends current HTML content to backend
   * 2. Receives generated file blob
   * 3. Creates downloadable browser URL
   * 4. Triggers file download
   *
   * @async
   * @param {number} exportType - Export type identifier
   * @param {string} extension - File extension to download
   * @returns {Promise<void>}
   */
  const handleExport = async (exportType, extension) => {
    try {
      const response = await exportRteWordPdf({
        htmlContent: value,
        exportType,
      });

      const url = window.URL.createObjectURL(response);

      const link = document.createElement('a');

      link.href = url;
      link.download = `richtexteditor-export.${extension}`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

   /**
   * Handles toolbar button actions.
   *
   * Intercepts Syncfusion export actions and replaces
   * default behavior with custom backend export handling.
   *
   * Supported actions:
   * - ExportWord
   * - ExportPdf
   *
   * @async
   * @param {Object} args - Toolbar click event arguments
   * @returns {Promise<void>}
   */
  const toolbarClick = async (args) => {
    if (args.item.subCommand === 'ExportWord') {
      // Prevent Syncfusion default export behavior
      args.cancel = true;

      await handleExport(1, 'docx');
    }

    if (args.item.subCommand === 'ExportPdf') {
      // Prevent Syncfusion default export behavior
      args.cancel = true;

      await handleExport(2, 'pdf');
    }
  };

   /**
   * Default toolbar configuration for the rich text editor.
   *
   * Includes:
   * - Text formatting
   * - Font controls
   * - Import/export actions
   * - Lists and alignment
   * - Media insertion
   * - Source code editing
   * - Undo/redo controls
   */
  let toolbarSettings = {
    enableFloating: true,
    items: [
      'Bold',
      'Italic',
      'Underline',
      'StrikeThrough',
      'FontName',
      'FontSize',
      'FontColor',
      'BackgroundColor',
      'LowerCase',
      'UpperCase',
      '|',
      'ImportWord',
      'ExportWord',
      'ExportPdf',
      '|',
      'Formats',
      'Alignments',
      'OrderedList',
      'UnorderedList',
      'Outdent',
      'Indent',
      '|',
      'CreateLink',
      'Image',
      '|',
      'ClearFormat',
      'Print',
      'SourceCode',
      'FullScreen',
      '|',
      'Undo',
      'Redo',
    ],
    type: 'Scrollable',
  };

  return (
    <div>
      <div className={'d-flex position-relative'}>
        {label && (
          <div className={'text-area-label'}>
            {label}
            {asteriskOn && <span style={{ color: '#FF0000' }}>*</span>}
          </div>
        )}

        {showIncludeSwitch && (
          <div style={{ marginTop: '-3px', position: 'absolute', right: '0' }}>
            <Switch
              active={activeSwitch}
              label={'Include'}
              onSwitch={onSwitch}
            />
          </div>
        )}
      </div>

      <div className="rte-wrapper">
        <RichTextEditorComponent
          id={name}
          change={(args) => {
            onChange({
              name,
              value: args.value,
            });
          }}
          disabled={disabled}
          insertImageSettings={{
            saveFormat: 'Base64',
          }}
          name={name}
          toolbarClick={toolbarClick}
          toolbarSettings={toolbarSettings}
          style={style}
          value={value}
          importWord={{
            serviceUrl: `${process.env.REACT_APP_HOST_URL}Campaign/import-word`,
          }}
        >
          <Inject
            services={[
              HtmlEditor,
              ImportExport,
              Image,
              Link,
              QuickToolbar,
              Toolbar,
            ]}
          />
        </RichTextEditorComponent>
      </div>

      {errorMessage && (
        <small className={'compose-email-form-error-message'}>
          {errorMessage}
        </small>
      )}
    </div>
  );
}

export default RichTextEditorInput;
